import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subject, catchError, debounceTime, distinctUntilChanged, map, of, takeUntil } from 'rxjs';
import { StyleConfigEditorComponent } from '../form-settings/style-config-editor.component';
import { FormSettingModel } from '../../../models/form.model';

import { FormBuilderService } from '../../../service/form-builder.service';
import { MarketingFormsService, CrmStandardField, CatalogOption } from '../../../service/marketing-forms.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { extractApiErrorMessage } from '../../../utils/api-error.util';
import { FIELD_CAPS, FieldCapabilities, FillMode } from '../../../utils/field-caps.util';
import {
  FormFieldModel, FieldType, FormFieldOption,
  ConditionOperator, ConditionAction, ConditionLogic,
  ConditionalRule, CrmObjectType,
} from '../../../models/form-field.model';

@Component({
  selector: 'app-field-properties',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, StyleConfigEditorComponent],
  templateUrl: './field-properties.component.html',
})
export class FieldPropertiesComponent implements OnInit, OnDestroy {
  @Output() styleSettingsChange = new EventEmitter<Partial<FormSettingModel>>();

  private readonly destroy$ = new Subject<void>();
  private ignoreChanges = false;

  submitButtonLabel = '';
  styleConfig = '';

  form = new FormGroup({
    label:              new FormControl(''),
    placeholder:        new FormControl(''),
    helpText:           new FormControl(''),
    isRequired:         new FormControl(false),
    isHidden:           new FormControl(false),
    columnSpan:         new FormControl(2),
    stepNumber:         new FormControl(1),
    defaultValueSource: new FormControl(''),
    defaultFixedValue:  new FormControl(''),
    urlParamName:       new FormControl(''),
  });

  // ── Capacidades por tipo de campo ──────────────────────────────────────────
  // Fuente de verdad centralizada; todos los getters de visibilidad del template
  // derivan de aquí. Cambia el tipo → el panel se adapta solo.

  get caps(): FieldCapabilities | null {
    return this.selectedField ? FIELD_CAPS[this.selectedField.fieldType] : null;
  }

  // Getters de visibilidad derivados de caps (usados en el template).
  get showPlaceholder():       boolean { return this.caps?.placeholder       ?? false; }
  get showHelpText():          boolean { return this.caps?.helpText          ?? false; }
  get showRequired():          boolean { return this.caps?.required          ?? false; }
  get showColumnSpan():        boolean { return this.caps?.columnSpan        ?? false; }
  get hasOptions():            boolean { return this.caps?.options           ?? false; }
  get showTextValidation():    boolean { return this.caps?.validation === 'text';   }
  get showNumberValidation():  boolean { return this.caps?.validation === 'number'; }
  get showConditionalRules():  boolean { return this.caps?.conditionalRules  ?? false; }
  get showAdvancedSection():   boolean {
    if (!this.caps) return false;
    return this.caps.validation !== 'none' || this.caps.conditionalRules;
  }

  // ── Modo de llenado ────────────────────────────────────────────────────────
  fillMode: FillMode = 'USER';

  readonly fillModes: { value: FillMode; label: string; help: string }[] = [
    { value: 'USER',  label: 'Lo escribe el usuario',
      help: 'El visitante ve el campo y lo completa.' },
    { value: 'FIXED', label: 'Valor predeterminado (oculto)',
      help: 'El campo no se muestra al visitante; se envía este valor fijo. Puedes mapearlo a una propiedad del CRM (ej. Origen).' },
    { value: 'URL',   label: 'Desde parámetro de URL (oculto)',
      help: 'El campo no se muestra; toma su valor del parámetro indicado en la URL (ej. utm_source).' },
  ];

  /** Modos permitidos para el tipo de campo activo (subconjunto de fillModes). */
  get filteredFillModes(): { value: FillMode; label: string; help: string }[] {
    const allowed = this.caps?.fillModes;
    if (!allowed?.length) return [];
    return this.fillModes.filter(m => allowed.includes(m.value));
  }

  get isFixedMode(): boolean { return this.fillMode === 'FIXED'; }
  get isUrlMode():   boolean { return this.fillMode === 'URL';   }

  /**
   * Sincroniza los controles del formulario con el modo elegido.
   * `opts.emitEvent = false` → solo actualización visual sin disparar valueChanges
   * (usado durante la carga del campo para no marcar dirty ni encolar un autosave).
   */
  private applyFillModeControls(
    mode: FillMode,
    opts: { emitEvent: boolean } = { emitEvent: true },
  ): void {
    switch (mode) {
      case 'USER':
        this.form.patchValue(
          { isHidden: false, defaultValueSource: null, defaultFixedValue: '', urlParamName: '' },
          opts,
        );
        break;
      case 'FIXED':
        this.form.patchValue(
          { isHidden: true, defaultValueSource: 'fixed', urlParamName: '' },
          opts,
        );
        break;
      case 'URL':
        this.form.patchValue(
          { isHidden: true, defaultValueSource: 'url_parameter', defaultFixedValue: '' },
          opts,
        );
        break;
    }
  }


  // ── Mapeo CRM del campo ("¿A qué dato del CRM va?") ──────────────────────────
  crmObjectType: CrmObjectType | '' = '';
  crmIsCustom = false;
  crmStandardField: string | undefined;
  standardFields: CrmStandardField[] = [];
  standardFieldsLoaded = false;

  private static readonly COMPANY_CATALOG_LOCK: Record<string, { catalog: string; label: string }> = {
    'id_company_tax_type': { catalog: 'company_tax_type', label: 'Tipo de Identificación Fiscal' },
    'id_company_size':     { catalog: 'company_size',     label: 'Tamaño'                        },
    'id_company_sector':   { catalog: 'company_sector',   label: 'Sector / Industria'             },
  };

  get isCatalogLocked(): boolean {
    const ft = this.selectedField?.fieldType;
    return (ft === 'DROPDOWN' || ft === 'CHECKBOXES' || ft === 'RADIO')
      && this.crmObjectType === 'COMPANY'
      && (this.crmStandardField ?? '') in FieldPropertiesComponent.COMPANY_CATALOG_LOCK;
  }

  get catalogLockedLabel(): string {
    return FieldPropertiesComponent.COMPANY_CATALOG_LOCK[this.crmStandardField ?? '']?.label ?? '';
  }

  private syncCatalogOptions(): void {
    const info = FieldPropertiesComponent.COMPANY_CATALOG_LOCK[this.crmStandardField ?? ''];
    if (!info) return;
    this.formsService.getCatalogOptions(info.catalog).pipe(
      catchError(() => of([] as CatalogOption[])),
      takeUntil(this.destroy$),
    ).subscribe(opts => {
      const mapped = opts.map((o, i) => ({
        optionLabel: o.label,
        optionValue: o.value,
        sortOrder:   i,
        isDefault:   false,
      }));
      const currentValues = this.options.map(o => o.optionValue).join(',');
      const newValues     = mapped.map(o => o.optionValue).join(',');
      if (currentValues === newValues) return;
      this.options = mapped;
      this.syncOptions();
    });
  }

  private resetCrmState(): void {
    this.crmObjectType = '';
    this.crmIsCustom = false;
    this.crmStandardField = undefined;
    this.standardFields = [];
    this.standardFieldsLoaded = false;
  }

  private loadCrmMapping(): void {
    const field = this.selectedField;
    if (!field?.id) return;
    const fid = field.id;
    this.formsService.getFieldCrmMap(field.formId, fid).pipe(
      catchError(() => of(null)),
      takeUntil(this.destroy$),
    ).subscribe(mapping => {
      if (this.selectedField?.id !== fid) return;
      if (!mapping || !mapping.crmObjectType) return;
      this.crmObjectType = mapping.crmObjectType;
      this.crmIsCustom = !!mapping.isCustomAttribute;
      this.crmStandardField = mapping.crmStandardField ?? undefined;
      this.loadStandardFields(mapping.crmObjectType);
      if (this.isCatalogLocked) this.syncCatalogOptions();
    });
  }

  private loadStandardFields(objectType: CrmObjectType): void {
    const fid = this.selectedField?.id;
    this.standardFieldsLoaded = false;
    this.formsService.getCrmStandardFields(objectType).pipe(
      catchError(() => of([] as CrmStandardField[])),
      takeUntil(this.destroy$),
    ).subscribe(list => {
      if (this.selectedField?.id !== fid || this.crmObjectType !== objectType) return;
      this.standardFields = list ?? [];
      this.standardFieldsLoaded = true;
    });
  }

  private static readonly URL_SOURCES = new Set([
    'url_parameter', 'utm_source', 'utm_medium', 'utm_campaign',
    'utm_term', 'utm_content', 'page_url',
  ]);

  private deriveFillMode(f: FormFieldModel): FillMode {
    const src = f.defaultValueSource ?? '';
    if (f.isHidden && FieldPropertiesComponent.URL_SOURCES.has(src)) return 'URL';
    if (f.isHidden && src === 'fixed')                               return 'FIXED';
    if (f.fieldType === 'HIDDEN' && !src)                            return 'FIXED';
    return 'USER';
  }

  onFillModeChange(mode: FillMode): void {
    this.fillMode = mode;
    if (this.selectedKey == null) return;
    this.applyFillModeControls(mode);
  }

  selectedField: FormFieldModel | null = null;
  selectedKey: string | null = null;
  options: FormFieldOption[] = [];
  panelMode: 'field' | 'design' = 'field';
  advancedOpen = false;
  validationRules = '';
  conditionalRules = '';

  valMinLength: number | null = null;
  valMaxLength: number | null = null;
  valPattern = '';
  valMin: number | null = null;
  valMax: number | null = null;

  condActive = false;
  condRule: ConditionalRule = {
    action: 'show',
    logic: 'and',
    conditions: [{ field_key: '', operator: 'equals', value: '' }],
  };

  readonly condActions: { value: ConditionAction; label: string }[] = [
    { value: 'show',     label: 'Mostrar' },
    { value: 'hide',     label: 'Ocultar' },
    { value: 'required', label: 'Obligatorio' },
  ];

  readonly condLogics: { value: ConditionLogic; label: string }[] = [
    { value: 'and', label: 'Todas (Y)' },
    { value: 'or',  label: 'Alguna (O)' },
  ];

  readonly allCondOperators: { value: ConditionOperator; label: string }[] = [
    { value: 'equals',       label: 'Es igual a' },
    { value: 'not_equals',   label: 'No es igual a' },
    { value: 'contains',     label: 'Contiene' },
    { value: 'greater_than', label: 'Mayor que' },
    { value: 'less_than',    label: 'Menor que' },
    { value: 'not_empty',    label: 'No está vacío' },
    { value: 'is_empty',     label: 'Está vacío' },
  ];

  condOperatorsFor(fieldKey: string): { value: ConditionOperator; label: string }[] {
    const trigger = this.condTriggerField(fieldKey);
    if (!trigger) return this.allCondOperators;
    const type = trigger.fieldType;

    if (type === 'NUMBER' || type === 'DATE' || type === 'RATING_NPS') {
      return [
        { value: 'equals',       label: 'Es igual a' },
        { value: 'not_equals',   label: 'No es igual a' },
        { value: 'greater_than', label: 'Mayor que' },
        { value: 'less_than',    label: 'Menor que' },
        { value: 'not_empty',    label: 'No está vacío' },
        { value: 'is_empty',     label: 'Está vacío' },
      ];
    }

    if (type === 'DROPDOWN' || type === 'RADIO' || type === 'CHECKBOXES') {
      return [
        { value: 'equals',     label: 'Es igual a' },
        { value: 'not_equals', label: 'No es igual a' },
        { value: 'not_empty',  label: 'No está vacío' },
        { value: 'is_empty',   label: 'Está vacío' },
      ];
    }

    return [
      { value: 'equals',     label: 'Es igual a' },
      { value: 'not_equals', label: 'No es igual a' },
      { value: 'contains',   label: 'Contiene' },
      { value: 'not_empty',  label: 'No está vacío' },
      { value: 'is_empty',   label: 'Está vacío' },
    ];
  }

  needsValue(op: ConditionOperator): boolean {
    return op !== 'not_empty' && op !== 'is_empty';
  }

  condTriggerField(fieldKey: string): FormFieldModel | undefined {
    return this.otherFields.find(f => f.fieldKey === fieldKey);
  }

  condTriggerHasOptions(fieldKey: string): boolean {
    const f = this.condTriggerField(fieldKey);
    return f ? FIELD_CAPS[f.fieldType].options : false;
  }

  condTriggerOptions(fieldKey: string): FormFieldOption[] {
    return this.condTriggerField(fieldKey)?.options ?? [];
  }

  condTriggerIsNumber(fieldKey: string): boolean {
    return this.condTriggerField(fieldKey)?.fieldType === 'NUMBER';
  }

  condTriggerIsDate(fieldKey: string): boolean {
    return this.condTriggerField(fieldKey)?.fieldType === 'DATE';
  }

  get otherFields(): FormFieldModel[] {
    const sel = this.selectedField;
    if (!sel) return [];
    const step = sel.stepNumber ?? 1;
    return this.builder.snapshot.fields.filter(
      f => f.fieldKey !== sel.fieldKey && (f.stepNumber ?? 1) === step,
    );
  }

  constructor(
    readonly builder: FormBuilderService,
    private formsService: MarketingFormsService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    const snap = this.builder.snapshot.settings;
    this.submitButtonLabel = snap?.submitButtonLabel ?? '';
    this.styleConfig = snap?.styleConfig ?? '';

    this.builder.state.pipe(
      map(s => s.selectedFieldKey),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(key => {
      this.selectedKey = key;
      this.selectedField = key != null
        ? this.builder.snapshot.fields.find(f => f.fieldKey === key) ?? null
        : null;
      this.resetCrmState();

      if (this.selectedField) {
        this.ignoreChanges = true;
        this.form.patchValue({
          label:              this.selectedField.label,
          placeholder:        this.selectedField.placeholder ?? '',
          helpText:           this.selectedField.helpText ?? '',
          isRequired:         this.selectedField.isRequired,
          isHidden:           this.selectedField.isHidden,
          columnSpan:         this.selectedField.columnSpan,
          stepNumber:         this.selectedField.stepNumber ?? 1,
          defaultValueSource: this.selectedField.defaultValueSource ?? '',
          defaultFixedValue:  this.selectedField.defaultFixedValue ?? '',
          urlParamName:       this.selectedField.urlParamName ?? '',
        }, { emitEvent: false });

        const derived = this.deriveFillMode(this.selectedField);
        const allowed = FIELD_CAPS[this.selectedField.fieldType].fillModes;

        // Guard: corrects legacy data where a field type has an incompatible fill mode
        // stored in the DB (e.g. GDPR_CONSENT with isHidden:true). The correction is
        // visual-only (emitEvent:false, inside ignoreChanges) — it does NOT trigger an
        // autosave. The corrected values will be persisted only on the next explicit save.
        const invalid = allowed.length > 0 && !allowed.includes(derived);
        const shouldForceUser = allowed.length === 0 && derived !== 'USER';
        if (invalid || shouldForceUser) {
          const corrected: FillMode = allowed.length > 0 ? allowed[0] : 'USER';
          this.fillMode = corrected;
          this.applyFillModeControls(corrected, { emitEvent: false });
        } else {
          this.fillMode = derived;
        }

        this.options = [...(this.selectedField.options ?? [])];
        this.validationRules  = this.selectedField.validationRules  ?? '';
        this.conditionalRules = this.selectedField.conditionalRules ?? '';
        this.parseValidationRules();
        this.parseConditionalRules();
        this.ignoreChanges = false;
        this.loadCrmMapping();
      }
    });

    this.builder.catalogSynced$.pipe(
      takeUntil(this.destroy$),
    ).subscribe(event => {
      if (event.fieldKey !== this.selectedKey) return;
      this.options = [...event.options];
    });

    this.form.valueChanges.pipe(
      debounceTime(200),
      takeUntil(this.destroy$),
    ).subscribe(values => {
      if (this.ignoreChanges || this.selectedKey == null) return;
      this.builder.updateField(this.selectedKey, {
        label:       values.label       ?? '',
        placeholder: values.placeholder ?? '',
        helpText:    values.helpText    ?? '',
        isRequired:  values.isRequired  ?? false,
        isHidden:    values.isHidden    ?? false,
        columnSpan:  values.columnSpan  ?? 2,
        stepNumber:  values.stepNumber  ?? 1,
        defaultValueSource: values.defaultValueSource || null,
        defaultFixedValue:  values.defaultFixedValue  ?? '',
        urlParamName:       values.urlParamName        ?? '',
      });
    });

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Options ───────────────────────────────────────────────────────────────

  addOption(): void {
    const n = this.options.length + 1;
    this.options = [...this.options, {
      optionLabel: `Opción ${n}`,
      optionValue: `opcion_${n}`,
      sortOrder: n - 1,
      isDefault: false,
    }];
    this.syncOptions();
  }

  updateOption(i: number, field: 'optionLabel' | 'optionValue', value: string): void {
    this.options = this.options.map((o, idx) => idx === i ? { ...o, [field]: value } : o);
    this.syncOptions();
  }

  removeOption(i: number): void {
    this.options = this.options.filter((_, idx) => idx !== i);
    this.syncOptions();
  }

  private syncOptions(): void {
    if (this.selectedKey == null) return;
    const current = this.builder.snapshot.fields.find(f => f.fieldKey === this.selectedKey);
    const stateOptions = current?.options ?? [];
    const merged = this.options.map((opt, i) => {
      const stateOpt = stateOptions[i];
      return (stateOpt?.id && !opt.id) ? { ...opt, id: stateOpt.id } : opt;
    });
    this.builder.updateField(this.selectedKey, { options: merged });
  }

  // ── Validation rules ──────────────────────────────────────────────────────

  private parseValidationRules(): void {
    try {
      const obj = this.validationRules ? JSON.parse(this.validationRules) : {};
      this.valMinLength = obj.minLength ?? null;
      this.valMaxLength = obj.maxLength ?? null;
      this.valPattern   = obj.pattern   ?? '';
      this.valMin       = obj.min       ?? null;
      this.valMax       = obj.max       ?? null;
    } catch {
      this.valMinLength = null; this.valMaxLength = null;
      this.valPattern   = '';   this.valMin = null; this.valMax = null;
    }
  }

  private serializeValidationRules(): void {
    const obj: Record<string, unknown> = {};
    if (this.valMinLength != null) obj['minLength'] = this.valMinLength;
    if (this.valMaxLength != null) obj['maxLength'] = this.valMaxLength;
    if (this.valPattern?.trim())   obj['pattern']   = this.valPattern.trim();
    if (this.valMin != null)       obj['min']        = this.valMin;
    if (this.valMax != null)       obj['max']        = this.valMax;
    const json = Object.keys(obj).length ? JSON.stringify(obj) : '';
    this.validationRules = json;
    if (this.selectedKey == null) return;
    this.builder.updateField(this.selectedKey, { validationRules: json || '' });
  }

  onValMinLengthChange(v: string): void { this.valMinLength = v !== '' ? Number(v) : null; this.serializeValidationRules(); }
  onValMaxLengthChange(v: string): void { this.valMaxLength = v !== '' ? Number(v) : null; this.serializeValidationRules(); }
  onValPatternChange(v: string): void   { this.valPattern = v; this.serializeValidationRules(); }
  onValMinChange(v: string): void       { this.valMin = v !== '' ? Number(v) : null; this.serializeValidationRules(); }
  onValMaxChange(v: string): void       { this.valMax = v !== '' ? Number(v) : null; this.serializeValidationRules(); }

  get regexPatternError(): string | null {
    if (!this.valPattern || !this.valPattern.trim()) return null;
    try {
      new RegExp(this.valPattern.trim());
      return null;
    } catch (e: any) {
      return e?.message || 'Expresión regular inválida';
    }
  }

  // ── Conditional rules ─────────────────────────────────────────────────────

  private parseConditionalRules(): void {
    if (!this.conditionalRules) {
      this.condActive = false;
      this.condRule = { action: 'show', logic: 'and', conditions: [{ field_key: '', operator: 'equals', value: '' }] };
      return;
    }
    try {
      const obj = JSON.parse(this.conditionalRules);
      if (obj?.action && obj?.logic && Array.isArray(obj?.conditions)) {
        this.condActive = true;
        this.condRule = {
          action:     obj.action,
          logic:      obj.logic,
          conditions: obj.conditions.length
            ? obj.conditions.map((c: Record<string, string>) => ({
                field_key: c['field_key'] ?? '',
                operator:  (c['operator'] ?? 'equals') as ConditionOperator,
                value:     c['value'] ?? '',
              }))
            : [{ field_key: '', operator: 'equals' as ConditionOperator, value: '' }],
        };
        return;
      }
      // Legacy schema: { showIf: { field, operator, value } }
      if (obj?.showIf) {
        const si = obj.showIf as Record<string, string>;
        const legacyOp = ['equals','not_equals','contains','not_empty'].find(op => si[op] !== undefined);
        this.condActive = true;
        this.condRule = {
          action: 'show', logic: 'and',
          conditions: [{
            field_key: si['field'] ?? '',
            operator:  (si['operator'] ?? legacyOp ?? 'equals') as ConditionOperator,
            value:     si['value'] ?? (legacyOp ? si[legacyOp] : '') ?? '',
          }],
        };
        return;
      }
    } catch { /* fall through */ }
    this.condActive = false;
    this.condRule = { action: 'show', logic: 'and', conditions: [{ field_key: '', operator: 'equals', value: '' }] };
  }

  get invalidConditionCount(): number {
    if (!this.condActive) return 0;
    return this.condRule.conditions.filter(c => !this.isConditionValid(c)).length;
  }

  private isConditionValid(c: { field_key: string; operator: ConditionOperator }): boolean {
    if (!c.field_key || !c.field_key.trim()) return false;
    return this.condOperatorsFor(c.field_key).some(op => op.value === c.operator);
  }

  private serializeConditionalRules(): void {
    if (!this.condActive) {
      this.conditionalRules = '';
      if (this.selectedKey != null) this.builder.updateField(this.selectedKey, { conditionalRules: '' });
      return;
    }
    const validConditions = this.condRule.conditions.filter(c => this.isConditionValid(c));
    if (validConditions.length === 0) {
      this.conditionalRules = '';
      if (this.selectedKey != null) this.builder.updateField(this.selectedKey, { conditionalRules: '' });
      return;
    }
    const payloadRule = { ...this.condRule, conditions: validConditions };
    const json = JSON.stringify(payloadRule);
    this.conditionalRules = json;
    if (this.selectedKey != null) this.builder.updateField(this.selectedKey, { conditionalRules: json });
  }

  onCondActiveChange(active: boolean): void {
    this.condActive = active;
    this.serializeConditionalRules();
  }

  onCondActionChange(action: string): void {
    this.condRule = { ...this.condRule, action: action as ConditionAction };
    this.serializeConditionalRules();
  }

  onCondLogicChange(logic: string): void {
    this.condRule = { ...this.condRule, logic: logic as ConditionLogic };
    this.serializeConditionalRules();
  }

  addCondition(): void {
    this.condRule = {
      ...this.condRule,
      conditions: [...this.condRule.conditions, { field_key: '', operator: 'equals', value: '' }],
    };
    this.serializeConditionalRules();
  }

  removeCondition(i: number): void {
    if (this.condRule.conditions.length <= 1) return;
    this.condRule = {
      ...this.condRule,
      conditions: this.condRule.conditions.filter((_, idx) => idx !== i),
    };
    this.serializeConditionalRules();
  }

  onConditionFieldChange(i: number, fieldKey: string): void {
    this.condRule = {
      ...this.condRule,
      conditions: this.condRule.conditions.map((c, idx) =>
        idx === i ? { ...c, field_key: fieldKey, operator: 'equals', value: '' } : c),
    };
    this.serializeConditionalRules();
  }

  onConditionOperatorChange(i: number, operator: string): void {
    this.condRule = {
      ...this.condRule,
      conditions: this.condRule.conditions.map((c, idx) =>
        idx === i ? { ...c, operator: operator as ConditionOperator } : c),
    };
    this.serializeConditionalRules();
  }

  onConditionValueChange(i: number, value: string): void {
    this.condRule = {
      ...this.condRule,
      conditions: this.condRule.conditions.map((c, idx) => idx === i ? { ...c, value } : c),
    };
    this.serializeConditionalRules();
  }

  onSubmitLabelChange(label: string): void {
    this.submitButtonLabel = label;
    this.styleSettingsChange.emit({ submitButtonLabel: label });
  }

  onStyleConfigChange(json: string): void {
    this.styleConfig = json;
    this.styleSettingsChange.emit({ styleConfig: json });
  }

  toggleAdvanced(): void { this.advancedOpen = !this.advancedOpen; }

  columnOptions = [
    { value: 1, label: '1/3' },
    { value: 2, label: '2/3' },
    { value: 3, label: '3/3' },
  ];
}
